import StripsAdvanced from './StripsAdvanced';
import BoardCanvas from 'boardCanvas/BoardCanvas';
import {
  MaterialPatternString,
  parseSubBoardPatternString,
  spacePattern,
  SubBoardPatternString,
  toPatternString,
} from 'Pattern';
import { BoardType, Units } from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import { createAnonymousBoard } from 'boardClasses/BoardFactory';
import Strips from 'boardClasses/strips/Strips';
import BuildStep from 'designer/BuildStep';
import {
  getSubBoardThicknessesByKey,
  subpatternKeyToIndex,
} from 'utils/patternUtils';
import {
  getDisplayLengthInUnits,
  groupBy,
  mapMapValues,
  sortBy,
} from 'utils/utils';

type Props = {
  board: BoardBase<BoardType.StripsAdvanced>;
};

function StripsAdvancedBuildSteps(props: Props): JSX.Element {
  const { board } = props;

  getSubboards(board);

  const { subpatterns } = board.params;
  const pattern = parseSubBoardPatternString(board.params.pattern);
  const displayLength = getDisplayLengthInUnits(board.units);

  const subBoardThicknessesByKey = getSubBoardThicknessesByKey(pattern);
  const subBoardInfo = Object.entries(subBoardThicknessesByKey).reduce(
    (result, [subpatternKey, lengthsByThickness]) => {
      const resultsForKey: Array<[string, number, number]> = Object.entries(
        lengthsByThickness
      ).map(([thickness, length]): [string, number, number] => [
        subpatternKey,
        +thickness,
        +length,
      ]);

      return [...result, ...resultsForKey];
    },
    [] as Array<[string, number, number]>
  );

  const subBoardsSteps = subBoardInfo
    .map(([key, thickness, length]) => {
      const patternString = subpatterns[subpatternKeyToIndex(key)];
      const pattern = parseSubBoardPatternString(patternString);
      const step1PatternPre = Array.from(
        groupBy(sortBy(pattern, ['size']), 'key').values()
      ).flat();
      const step1Pattern = toPatternString(spacePattern(step1PatternPre));

      const firstStep = {
        title: `Cut sub-board ${key}-${displayLength(length)} strips`,
        text: '',
        board: createAnonymousBoard(
          Strips,
          {
            pattern: step1Pattern,
            numRows: 1,
            rowHeight: length,
            thickness: 0,
            flip: false,
          },
          Units.IN
        ),
      };
      const secondStep = {
        title: `Glue up sub-board ${key}-${displayLength(length)}`,
        text: '',
        board: createAnonymousBoard(
          Strips,
          {
            pattern: patternString,
            numRows: 1,
            rowHeight: length,
            thickness: 0,
            flip: false,
          },
          Units.IN
        ),
      };
      const thirdStep = {
        title: `Cross cuts for sub-board ${key}-${displayLength(length)}`,
        text: '',
        board: createAnonymousBoard(
          StripsAdvanced,
          {
            pattern: new Array(length / thickness)
              .fill(`${thickness}A 0.25`)
              .join(' ') as SubBoardPatternString,
            subpatterns: [patternString],
            thickness: 0,
          },
          Units.IN
        ),
      };

      return [firstStep, secondStep, thirdStep];
    })
    .flat();

  const steps = [
    {
      title: 'Overview',
      text: `Building this type of board is basically the same as building several "Strips" boards and then mixing parts of the boards together. For this board there are ${subBoardInfo.length} different "Strips" boards to build and combine.`,
      board: createAnonymousBoard(
        Strips,
        {
          pattern: '' as MaterialPatternString,
          numRows: 1,
          rowHeight: 1,
          thickness: 0,
          flip: false,
        },
        Units.IN
      ),
    },
    ...subBoardsSteps,
    {
      title: 'Arrange strips together',
      text: ``,
      board: board.updateParams({
        pattern: toPatternString(spacePattern(pattern)),
      }),
    },
    {
      title: 'Final glueup',
      text: ``,
      board,
    },
  ];

  return (
    <>
      {steps.map((step, index) => (
        <BuildStep
          key={index}
          title={step.title}
          content={step.text}
          preview={
            step.board && (
              <BoardCanvas
                dimensions={step.board.getDimensions()}
                renderables={step.board.getRenderables()}
                scaleFactor={24}
              />
            )
          }
        ></BuildStep>
      ))}
    </>
  );
}

export function getSubboards(
  board: BoardBase<BoardType.StripsAdvanced>
): Array<Strips> {
  const { subpatterns, thickness } = board.params;
  const pattern = parseSubBoardPatternString(board.params.pattern);

  const rowsByKeyAndSize = mapMapValues(
    groupBy(sortBy(pattern, ['key', 'size']), 'key'),
    (_, fragments) => groupBy(fragments, 'size')
  );

  const subboards: Array<Strips> = [];

  for (const [key, rowsBySize] of rowsByKeyAndSize) {
    for (const [size, rows] of rowsBySize) {
      if (key == null) {
        continue;
      }
      const subboard = createAnonymousBoard(
        Strips,
        {
          pattern: subpatterns[subpatternKeyToIndex(key)],
          numRows: rows.length,
          rowHeight: +size,
          thickness,
          flip: false,
        },
        board.units
      );

      subboards.push(subboard);
    }
  }

  return subboards;
}

export default StripsAdvancedBuildSteps;
